<template>
  <div class="user-management">
    <!-- Page Header -->
    <!-- <div class="page-header">
      <h1 class="page-title">User Management</h1>
      <p class="page-subtitle">Manage healthcare professionals and their account access</p>
    </div> -->

    <!-- Tabbed Stats Section -->
    <div class="stats-tabs-container">
      <div class="tab-buttons">
        <button 
          @click="activeTab = 'overview'" 
          :class="{ active: activeTab === 'overview' }"
          class="tab-btn"
        >
          Overview
        </button>
        <button 
          @click="activeTab = 'activity'" 
          :class="{ active: activeTab === 'activity' }"
          class="tab-btn"
        >
          User Activity
        </button>
        <button 
          @click="activeTab = 'tests'" 
          :class="{ active: activeTab === 'tests' }"
          class="tab-btn"
        >
          Test Analytics
        </button>
      </div>

      <!-- Overview Tab -->
      <div v-show="activeTab === 'overview'" class="stats-grid">
        <div class="stat-card">
          <div class="stat-number primary">{{ stats.totalUsers }}</div>
          <div class="stat-label">Total Users</div>
        </div>
        <div class="stat-card">
          <div class="stat-number success">{{ stats.activeUsers }}</div>
          <div class="stat-label">Active Users</div>
        </div>
        <div class="stat-card">
          <div class="stat-number warning">{{ stats.pendingUsers }}</div>
          <div class="stat-label">Pending Users</div>
        </div>
        <div class="stat-card">
          <div class="stat-number danger">{{ stats.suspendedUsers }}</div>
          <div class="stat-label">Suspended Users</div>
        </div>
      </div>

      <!-- User Activity Tab -->
      <div v-show="activeTab === 'activity'" class="stats-grid">
        <div class="stat-card">
          <div class="stat-text">{{ additionalStats?.lastLoggedInUser || 'None' }}</div>
          <div class="stat-label">Last Logged In User</div>
        </div>
        <div class="stat-card">
          <div class="stat-number primary">{{ additionalStats?.usersLoggedInLast24Hours || 0 }}</div>
          <div class="stat-label">Logged In (24h)</div>
        </div>
        <div class="stat-card">
          <div class="stat-number success">{{ additionalStats?.userRegisteredThisWeek || 0 }}</div>
          <div class="stat-label">Registered This Week</div>
        </div>
        <div class="stat-card">
          <div class="stat-number warning">{{ additionalStats?.userRegisteredThisMonth || 0 }}</div>
          <div class="stat-label">Registered This Month</div>
        </div>
      </div>

      <!-- Test Analytics Tab -->
      <div v-show="activeTab === 'tests'" class="stats-grid">
        <div class="stat-card">
          <div class="stat-number primary">{{ additionalStats?.testsStartedToday || 0 }}</div>
          <div class="stat-label">Tests Started Today</div>
        </div>
        <div class="stat-card">
          <div class="stat-number success">{{ additionalStats?.testsStartedThisWeek || 0 }}</div>
          <div class="stat-label">Tests Started This Week</div>
        </div>
        <div class="stat-card">
          <div class="stat-number warning">{{ additionalStats?.testsCompletedToday || 0 }}</div>
          <div class="stat-label">Tests Completed Today</div>
        </div>
        <div class="stat-card">
          <div class="stat-number danger">{{ additionalStats?.testsCompletedThisWeek || 0 }}</div>
          <div class="stat-label">Tests Completed This Week</div>
        </div>
      </div>
    </div>

    <!-- Controls Section -->
    <div class="controls-section">
      <div class="search-filters">
        <div class="search-box">
          <input 
            type="text" 
            placeholder="Search by name, email, or login..." 
            class="search-input"
            v-model="searchQuery"
            @input="debouncedSearch"
          >
          <svg class="search-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
        <select class="filter-select" v-model="roleFilter" @change="loadUsers">
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="expert">Expert</option>
          <option value="user">User</option>
        </select>
        <select class="filter-select" v-model="statusFilter" @change="loadUsers">
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">In Active</option>
        </select>
        <select class="filter-select" v-model="verificationFilter" @change="loadUsers">
          <option value="">Email Verification</option>
          <option value="true">Verified</option>
          <option value="false">Pending Verification</option>
        </select>
        <select class="filter-select" v-model="dateFilter" @change="loadUsers">
          <option value="">All Time</option>
          <option value="today">Today</option>
          <option value="thisweek">This Week</option>
          <option value="thismonth">This Month</option>
        </select>
        <select class="filter-select" v-model="testsFilter" @change="loadUsers">
          <option value="">All Users</option>
          <option value="true">Has Tests</option>
          <option value="false">No Tests</option>
        </select>
      </div>
      <div class="action-buttons">
        <button class="btn btn-primary" @click="showAddUserModal = true" :disabled="loading">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          Add User
        </button>
        <button class="btn btn-secondary" @click="exportUsers" :disabled="loading">
          Export Users
        </button>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmModal" class="modal-overlay" @click="cancelConfirmation">
      <div class="modal-container confirmation-modal" @click.stop>
        <div class="modal-header">
          <div class="confirmation-icon" :class="confirmationData.type">
            <svg v-if="confirmationData.type === 'danger'" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <svg v-else-if="confirmationData.type === 'warning'" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <svg v-else width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h3 class="confirmation-title">{{ confirmationData.title }}</h3>
        </div>
        
        <div class="confirmation-body">
          <p class="confirmation-message">{{ confirmationData.message }}</p>
          <p v-if="confirmationData.details" class="confirmation-details">{{ confirmationData.details }}</p>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="cancelConfirmation">
            Cancel
          </button>
          <button 
            type="button" 
            class="btn"
            :class="confirmationData.type === 'danger' ? 'btn-danger' : 'btn-primary'"
            @click="confirmAction"
            :disabled="confirmationLoading"
          >
            <svg v-if="confirmationLoading" class="spinner" width="16" height="16" viewBox="0 0 24 24">
              <circle class="spinner-path" cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
            </svg>
            {{ confirmationLoading ? 'Processing...' : confirmationData.confirmText }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add User Modal -->
    <div v-if="showAddUserModal" class="modal-overlay">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h2 class="modal-title">Add New User</h2>
          <button class="modal-close-btn" @click="closeAddUserModal">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="submitAddUser" class="add-user-form">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label" for="firstName">First Name *</label>
              <input 
                type="text" 
                id="firstName"
                v-model="newUser.firstName" 
                class="form-input" 
                required
                :class="{ 'error': formErrors.firstName }"
              >
              <span v-if="formErrors.firstName" class="error-message">{{ formErrors.firstName }}</span>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="lastName">Last Name *</label>
              <input 
                type="text" 
                id="lastName"
                v-model="newUser.lastName" 
                class="form-input" 
                required
                :class="{ 'error': formErrors.lastName }"
              >
              <span v-if="formErrors.lastName" class="error-message">{{ formErrors.lastName }}</span>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label" for="email">Email Address *</label>
            <input 
              type="email" 
              id="email"
              v-model="newUser.email" 
              class="form-input" 
              required
              :class="{ 'error': formErrors.email }"
            >
            <span v-if="formErrors.email" class="error-message">{{ formErrors.email }}</span>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label" for="phone">Phone Number</label>
              <input 
                type="tel" 
                id="phone"
                v-model="newUser.phone" 
                class="form-input"
                :class="{ 'error': formErrors.phone }"
              >
              <span v-if="formErrors.phone" class="error-message">{{ formErrors.phone }}</span>
            </div>

            <div class="form-group">
              <label class="form-label" for="role">Role *</label>
              <select 
                id="role"
                v-model="newUser.role" 
                class="form-select" 
                required
                :class="{ 'error': formErrors.role }"
              >
                <option value="">Select Role</option>
                <option value="user">User</option>
                <option value="expert">Expert</option>
                <option value="admin">Admin</option>
              </select>
              <span v-if="formErrors.role" class="error-message">{{ formErrors.role }}</span>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label" for="companyName">Company Name</label>
              <input 
                type="text" 
                id="companyName"
                v-model="newUser.companyName" 
                class="form-input"
                :class="{ 'error': formErrors.companyName }"
              >
              <span v-if="formErrors.companyName" class="error-message">{{ formErrors.companyName }}</span>
            </div>

            <div class="form-group">
              <label class="form-label" for="referrerSource">How did you hear about us?</label>
              <select 
                id="referrerSource"
                v-model="newUser.referrerSource" 
                class="form-select"
              >
                <option value="">Select Source</option>
                <option value="1">Website</option>
                <option value="2">Word of Mouth</option>
                <option value="3">Social Media</option>
                <option value="4">Conference</option>
                <option value="5">Colleague</option>
                <option value="6">Search Engine</option>
                <option value="7">Advertisement</option>
                <option value="8">Other</option>
              </select>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeAddUserModal">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="addingUser">
              <svg v-if="addingUser" class="spinner" width="16" height="16" viewBox="0 0 24 24">
                <circle class="spinner-path" cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
              </svg>
              {{ addingUser ? 'Adding...' : 'Add User' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading users...</p>
    </div>

    <!-- AG-Grid Container -->
    <div class="grid-container" v-else>
      <ag-grid-vue
        class="ag-theme-alpine ag-grid-custom"
        :columnDefs="columnDefs"
        :rowData="users"
        :defaultColDef="defaultColDef"
        :pagination="true"
        :paginationPageSize="10"
        :paginationPageSizeSelector="[10, 20,50,100]"
        :rowSelection="'multiple'"
        :animateRows="true"
        :suppressCellFocus="true"
        :getRowStyle="getRowStyle"
        @grid-ready="onGridReady"
      />
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <div 
        v-for="toast in toasts" 
        :key="toast.id"
        :class="['toast', toast.type, { show: toast.show }]"
      >
        {{ toast.message }}
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import { HttpHandler } from '@/http/HttpHandler';
import '@/assets/css/ag-grid-custom.css';

export default {
  name: 'AdminPanel',
  components: {
    AgGridVue
  },
  data() {
    return {
      activeTab: 'overview',
      gridApi: null,
      httpHandler: null,
      loading: false,
      searchQuery: '',
      roleFilter: '',
      idFilter :'',
      statusFilter: 'true',
      verificationFilter: '',
      dateFilter: '',
      testsFilter: '',
      // Add User Modal
      showAddUserModal: false,
      addingUser: false,
      newUser: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        referrerSource: '',
        role: ''
      },
      formErrors: {},

      // Confirmation Modal
      showConfirmModal: false,
      confirmationLoading: false,
      confirmationData: {
        title: '',
        message: '',
        details: '',
        confirmText: '',
        type: 'primary',
        action: null
      },

      toasts: [],
      toastIdCounter: 0,
      users: [],
      stats: {
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        suspendedUsers: 0
      },
      additionalStats: null,
      
      columnDefs: [
        { 
          field: 'id', 
          headerName: 'ID', 
          width: 80,
          filter: false,
        },
        { 
          field: 'name', 
          headerName: 'Name', 
          width: 200,
          cellRenderer: this.nameRenderer,
          filter: false,
        },
        { 
          field: 'email', 
          headerName: 'Email', 
          width: 220,
          filter: false,
        },
        { 
          field: 'role', 
          headerName: 'Role', 
          width: 100,
          cellRenderer: this.roleRenderer,
          filter: false,
        },
        { 
  field: 'numberOfTests', 
  headerName: 'No. of Tests', 
  width: 120,
  type: 'numericColumn',
  cellRenderer: (params) => {
    const { numberOfTests } = params.data;
    if (numberOfTests > 0) {
      return `<span style="color: #2f45a5; font-weight: 600; cursor: pointer; text-decoration: underline;">${numberOfTests}</span>`;
    }
    return numberOfTests;
  },
  cellStyle: { textAlign: 'center' },
  onCellClicked: (params) => {
    if (params.data.numberOfTests > 0) {
      this.$router.push({
        path: '/admin/TestDetails',
        query: { userId: params.data.id }
      });
    }
  },
  filter: false,
},
        { 
          field: 'lastLoginAt', 
          headerName: 'Last Logged In', 
          width: 160,
          cellRenderer: this.dateRenderer,
          filter: false,
        },
        { 
          field: 'isActive', 
          headerName: 'Status', 
          width: 120,
          cellRenderer: this.statusRenderer,
          filter: false,
        },
        {
          field: 'actions',
          headerName: 'Actions',
          width: 250,
          cellRenderer: this.actionsRenderer,
          sortable: false,
          filter: false,
        }
      ],
      defaultColDef: {
        sortable: true,
        filter: false,
        resizable: false,
        floatingFilter: false
      }
    };
  },
  
  created() {
    console.log('AdminPanel created() starting...');
    
    try {
      console.log('Creating HttpHandler instance...');
      this.httpHandler = new HttpHandler(this.$store);
      console.log('HttpHandler created successfully:', this.httpHandler);
      
      this.loadStats();
      this.loadUsers();
      
      this.debouncedSearch = this.debounce(this.loadUsers, 500);
      
      console.log('AdminPanel created() completed successfully');
      
    } catch (error) {
      console.error('Error in created():', error);
      this.showToast('error', 'Failed to initialize admin panel');
      this.loadMockData();
    }
  },
  
  methods: {
    async loadStats() {
      console.log('loadStats() called');
      
      if (!this.httpHandler || typeof this.httpHandler.getAdminUserStats !== 'function') {
        console.error('HttpHandler or getAdminUserStats method not available');
        this.additionalStats = {
          userRegisteredThisWeek: 0,
          testsCompletedThisWeek: 0,
          completionRateThisWeek: 0,
          lastLoggedInUser: 'No recent activity'
        };
        return;
      }
      
      try {
        console.log('Calling getAdminUserStats...');
        const statsData = await this.httpHandler.getAdminUserStats();
        console.log('Stats response:', statsData);
        
        this.stats = {
          totalUsers: statsData.totalUsers || 0,
          activeUsers: statsData.activeUsers || 0,
          pendingUsers: statsData.pendingUsers || 0,
          suspendedUsers: statsData.suspendedUsers || 0
        };
        
        this.additionalStats = {
          userRegisteredThisWeek: statsData.userRegisteredThisWeek || 0,
          userRegisteredThisMonth: statsData.userRegisteredThisMonth || 0,
          testsCompletedThisWeek: statsData.testsCompletedThisWeek || 0,
          testsCompletedThisMonth: statsData.testsCompletedThisMonth || 0,
          completionRateThisWeek: statsData.completionRateThisWeek || 0,
          completionRateThisMonth: statsData.completionRateThisMonth || 0,
          lastLoggedInUser: statsData.lastLoggedInUser || 'No recent activity'
        };
        
        console.log('Additional stats set:', this.additionalStats);
        
      } catch (error) {
        console.error('Error in loadStats:', error);
        
        this.additionalStats = {
          userRegisteredThisWeek: 0,
          testsCompletedThisWeek: 0,
          completionRateThisWeek: 0,
          lastLoggedInUser: 'API Error'
        };
        
        this.showToast('error', 'Failed to load user statistics');
      }
    },

    async loadUsers() {
      console.log('loadUsers() called');
      this.loading = true;
      
      if (!this.httpHandler || typeof this.httpHandler.getAdminUsers !== 'function') {
        console.error('HttpHandler or getAdminUsers method not available');
        this.loading = false;
        return;
      }
      
      try {
        const filters = {};
        
        if (this.searchQuery && this.searchQuery.trim() !== '') {
          filters.search = this.searchQuery.trim();
        }
        if (this.roleFilter && this.roleFilter !== '') {
          filters.roles = this.roleFilter;
        }
       
        if (this.statusFilter !== '') {
          filters.isActive = this.statusFilter === 'true';
        }
        else {
  // Explicitly set to true when no filter selected
  filters.isActive = true;
}
        if (this.verificationFilter !== '') {
          filters.isEmailVerified = this.verificationFilter === 'true';
        }
        if (this.dateFilter && this.dateFilter !== '') {
          filters.lastLogin = this.dateFilter;
        }
        if (this.testsFilter !== '') {
          filters.hasTests = this.testsFilter === 'true';
        }

        console.log('Calling getAdminUsers with filters:', filters);
        const response = await this.httpHandler.getAdminUsers(filters);
        console.log('Users response:', response);
        
        this.users = response.users || response.data || response || [];
        
        if (this.gridApi) {
          this.gridApi.setRowData(this.users);
        }
        
      } catch (error) {
        console.error('Error in loadUsers:', error);
        this.showToast('error', 'Failed to load users');
        this.users = [];
      } finally {
        this.loading = false;
      }
    },

    closeAddUserModal() {
      this.showAddUserModal = false;
      this.resetAddUserForm();
    },

    resetAddUserForm() {
      this.newUser = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        referrerSource: '',
        role: ''
      };
      this.formErrors = {};
    },

    validateForm() {
      this.formErrors = {};
      
      if (!this.newUser.firstName.trim()) {
        this.formErrors.firstName = 'First name is required';
      }
      
      if (!this.newUser.lastName.trim()) {
        this.formErrors.lastName = 'Last name is required';
      }
      
      if (!this.newUser.email.trim()) {
        this.formErrors.email = 'Email is required';
      } else if (!this.isValidEmail(this.newUser.email)) {
        this.formErrors.email = 'Please enter a valid email address';
      }
      
      if (!this.newUser.role) {
        this.formErrors.role = 'Role is required';
      }
      
      return Object.keys(this.formErrors).length === 0;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    async submitAddUser() {
      if (!this.validateForm()) {
        return;
      }

      this.addingUser = true;

      try {
        const userData = {
          firstName: this.newUser.firstName.trim(),
          lastName: this.newUser.lastName.trim(),
          email: this.newUser.email.trim(),
          phone: this.newUser.phone.trim(),
          companyName: this.newUser.companyName.trim(),
          referrerSource: this.newUser.referrerSource,
          role: this.newUser.role
        };

        await this.httpHandler.addAdminUser(userData);

        this.showToast('success', 'User added successfully!');
        this.closeAddUserModal();
        
        await Promise.all([
          this.loadUsers(),
          this.loadStats()
        ]);

      } catch (error) {
        console.error('Error adding user:', error);
        
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'Failed to add user. Please try again.';
        
        this.showToast('error', errorMessage);
      } finally {
        this.addingUser = false;
      }
    },

    showConfirmation(options) {
      this.confirmationData = {
        title: options.title || 'Confirm Action',
        message: options.message || 'Are you sure you want to continue?',
        details: options.details || '',
        confirmText: options.confirmText || 'Confirm',
        type: options.type || 'primary',
        action: options.action || null
      };
      this.showConfirmModal = true;
    },

    async confirmAction() {
      if (!this.confirmationData.action) return;
      
      this.confirmationLoading = true;
      
      try {
        await this.confirmationData.action();
        this.showConfirmModal = false;
      } catch (error) {
        console.error('Confirmation action failed:', error);
      } finally {
        this.confirmationLoading = false;
      }
    },

    cancelConfirmation() {
      this.showConfirmModal = false;
      this.confirmationLoading = false;
      this.confirmationData = {
        title: '',
        message: '',
        details: '',
        confirmText: '',
        type: 'primary',
        action: null
      };
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      params.api.sizeColumnsToFit();
    },
    
    nameRenderer(params) {
      const { name, surname, login } = params.data;
      const displayName = name && surname ? `${name} ${surname}` : login;
      const initials = name && surname ? 
        (name.charAt(0) + surname.charAt(0)).toUpperCase() : 
        login.substring(0, 2).toUpperCase();
      
      return `
        <div class="user-name-cell">
          <div class="user-grid-avatar">${initials}</div>
          <span>${displayName}</span>
        </div>
      `;
    },
    
    roleRenderer(params) {
      const role = params.value;
      return `<span class="role-badge role-${role.toLowerCase()}">${role}</span>`;
    },
    
    statusRenderer(params) {
      const { isActive, isVerified } = params.data;
      let status, statusClass;
      if(!isActive){
        status = 'SUSPENDED';
        statusClass = 'status-suspended';
      }
      else if (!isVerified) {
        status = 'Email Not Verified ';
        statusClass = 'status-pending';
      }
       else {
        status = 'Active';
        statusClass = 'status-active';
      }
      
      return `<span class="status-badge ${statusClass}">${status}</span>`;
    },
    
    dateRenderer(params) {
      if (!params.value) return 'Never';
      const date = new Date(params.value);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    actionsRenderer(params) {
      const { isActive, isEmailVerified, isVerified, id } = params.data;
      
      const verified = isEmailVerified !== undefined ? isEmailVerified : isVerified;
      
      let actionBtn = '';
      if (isActive) {
        actionBtn = `<button class="action-btn deactivate" onclick="window.adminPanelRef.toggleUserStatus(${id}, false)" data-user-id="${id}">Deactivate</button>`;
      } else {
        actionBtn = `<button class="action-btn activate" onclick="window.adminPanelRef.toggleUserStatus(${id}, true)" data-user-id="${id}">Activate</button>`;
      }
      
      let resendBtn = '';
      if (!verified) {
        resendBtn = `<button class="action-btn resend" onclick="window.adminPanelRef.resendVerification(${id})" data-user-id="${id}">Resend Link</button>`;
      }
      
      return `
        <div class="grid-actions">
          ${actionBtn}
          ${resendBtn}
        </div>
      `;
    },
    
    getRowStyle(params) {
      if (!params.data.isActive) {
        return { background: 'rgba(245, 158, 11, 0.03)' };
      }
      return { background: 'rgba(34, 197, 94, 0.03)' };
    },
    
    async toggleUserStatus(userId, activate) {
      const action = activate ? 'activate' : 'deactivate';
      const user = this.users.find(u => u.id === userId);
      const userName = user ? `${user.name || ''} ${user.surname || ''}`.trim() || user.email : 'this user';
      
      this.showConfirmation({
        title: activate ? 'Activate User' : 'Deactivate User',
        message: `Are you sure you want to ${action} ${userName}?`,
        details: activate 
          ? 'The user will regain access to the platform and be able to sign in.' 
          : 'The user will lose access to the platform and will not be able to sign in.',
        confirmText: activate ? 'Activate User' : 'Deactivate User',
        type: activate ? 'primary' : 'danger',
        action: async () => {
          try {
            if (this.httpHandler && typeof this.httpHandler.updateAdminUserStatus === 'function') {
              await this.httpHandler.updateAdminUserStatus(userId, activate);
            }
            
            const userIndex = this.users.findIndex(u => u.id === userId);
            if (userIndex !== -1) {
              this.users[userIndex].isActive = activate;
              
              if (this.gridApi) {
                this.gridApi.refreshCells({ force: true });
              }
            }
            
            this.showToast('success', `User ${activate ? 'activated' : 'deactivated'} successfully!`);
            
            await this.loadStats();
            
          } catch (error) {
            console.error('Error toggling user status:', error);
            this.showToast('error', `Failed to ${action} user`);
            throw error;
          }
        }
      });
    },
    
    async resendVerification(userId) {
      const user = this.users.find(u => u.id === userId);
      const userName = user ? `${user.name || ''} ${user.surname || ''}`.trim() || user.email : 'this user';
      
      this.showConfirmation({
        title: 'Resend Verification Email',
        message: `Send a new verification email to ${userName}?`,
        details: 'A verification link will be sent to their registered email address.',
        confirmText: 'Send Email',
        type: 'primary',
        action: async () => {
          try {
            if (this.httpHandler && typeof this.httpHandler.resendAdminUserVerification === 'function') {
              await this.httpHandler.resendAdminUserVerification(userId);
            }
            this.showToast('success', 'Verification email sent successfully!');
          } catch (error) {
            console.error('Error resending verification:', error);
            this.showToast('error', 'Failed to send verification email');
            throw error;
          }
        }
      });
    },
    
    exportUsers() {
      if (this.gridApi) {
        this.gridApi.exportDataAsCsv({
          fileName: 'proxymed_users_' + new Date().toISOString().split('T')[0] + '.csv',
          columnKeys: [
            'id',
            'name',
            'surname',
            'email',
            'role',
            'numberOfTests',
            'lastLoginAt',
            'isActive'
          ],
          processHeaderCallback: (params) => {
            if (params.column.getColId() === 'isActive') {
              return 'Active';
            }
            return params.column.getColDef().headerName || params.column.getColId();
          }
        });
        this.showToast('success', 'Export completed successfully!');
      }
    },

    async refreshData() {
      await Promise.all([
        this.loadStats(),
        this.loadUsers()
      ]);
      this.showToast('success', 'Data refreshed successfully!');
    },
    
    showToast(type, message) {
      const toast = {
        id: this.toastIdCounter++,
        type,
        message,
        show: false
      };
      
      this.toasts.push(toast);
      
      this.$nextTick(() => {
        toast.show = true;
      });
      
      setTimeout(() => {
        toast.show = false;
        setTimeout(() => {
          const index = this.toasts.findIndex(t => t.id === toast.id);
          if (index > -1) {
            this.toasts.splice(index, 1);
          }
        }, 300);
      }, 3000);
    },

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    loadMockData() {
      console.log('Loading mock data...');
      
      this.stats = {
        totalUsers: 4,
        activeUsers: 2,
        pendingUsers: 2,
        suspendedUsers: 0
      };
      
      this.additionalStats = {
        userRegisteredThisWeek: 2,
        testsCompletedThisWeek: 5,
        completionRateThisWeek: 85,
        lastLoggedInUser: "Demo User"
      };
      
      this.users = [
        {
          id: 1,
          login: "admin",
          name: "Admin",
          surname: "User",
          email: "admin@proxymed.com",
          role: "admin",
          isActive: true,
          isEmailVerified: true,
          numberOfTests: 247,
          lastLoggedIn: "2024-01-15T09:30:00"
        },
        {
          id: 2,
          login: "dr.smith",
          name: "Sarah",
          surname: "Smith",
          email: "sarah.smith@hospital.com",
          role: "expert",
          isActive: false,
          isEmailVerified: false,
          numberOfTests: 42,
          lastLoggedIn: "2024-01-14T16:45:00"
        }
      ];
      
      if (this.gridApi) {
        this.gridApi.setRowData(this.users);
      }
      
      this.loading = false;
      this.showToast('info', 'Demo data loaded');
    }
  },
  
  mounted() {
    window.adminPanelRef = this;
  },
  
  beforeDestroy() {
    if (window.adminPanelRef === this) {
      delete window.adminPanelRef;
    }
  }
};
</script>

<style scoped>
.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary {
  background: rgba(102, 123, 242, 0.1);
  color: #2f45a5;
  border: 1px solid rgba(102, 123, 242, 0.2);
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spinner-path {
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  stroke-linecap: round;
}
</style>